import Circle from '@mui/icons-material/Circle'
import { Alert, Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'

import { formatNumberAsPrice, isEmpty } from '@shared/common'
import { i18WithParams as t } from '@shared/locales'
import { CustomOrderAdminDto, CustomOrderClientDto, ReceiptDto } from 'service-api/src/types/api'
import { Spacer } from 'spacer/spacer'
import { calculateInvoicePayFacTotals, getInvoiceDepositTxns, isDeposit, paymentType } from '../../../shared/payment'
import { sumDepositCharges, getDepositLabel } from './helper'
import { DepositPoliciesDropdown } from './deposit_policies_dropdown'

export const Footer = ({
  receipt,
  dueToday,
  order,
}: {
  receipt: ReceiptDto
  dueToday?: number | null
  order: CustomOrderAdminDto | CustomOrderClientDto
}) => {
  const { palette } = useTheme()
  const dueTodayPercentage = useMemo(() => {
    if (!order.total || dueToday == undefined) return ''
    const percent = Math.round((dueToday / order.total) * 100)
    return `${percent}%`
  }, [dueToday, order.total])
  const isEstimatedTotal = receipt.invoices?.some((inv) => inv.items?.some((item) => item.priceVaries))

  const deposits = (order?.payments || []).filter(
    (payment) => payment.step === 'CONFIRMED' && isDeposit(payment) && payment.payFacType !== 'OFFLINE'
  )
  const storedPaymentMethod = deposits.find((d) => d.payFacType === 'ADYEN')?.appliedStoredPaymentMethod
  const paidToday = sumDepositCharges(deposits)
  const paidWithoutSurcharge = deposits.reduce((acc, deposit) => acc + (deposit.depositDue || 0), 0)
  const paidTodayPercentage = order.total ? `${Math.round((paidWithoutSurcharge / order.total) * 100)}%` : ''
  const orderCancelled = order?.orderStatus === 'CANCELED' || order?.orderStatus === 'PARTIALLY_CANCELED'
  const orderConfirmed = order?.orderStatus === 'CONFIRMED'
  const depositPolicies = (receipt.refundPolicyV2 || []).filter((entry) => entry.depositPolicy)
  const invoiceDepositTxns = useMemo(() => {
    return getInvoiceDepositTxns(order.payments)
  }, [order.payments])
  const invoicesWithRefund = order?.invoices?.filter((inv) => {
    if (inv.refunded) return true
    const txns = invoiceDepositTxns.get(inv.id || '')
    return txns?.some((t) => t.type === 'REFUND' && ((t.surcharge ?? 0) > 0 || (t.surchargeTax ?? 0) > 0))
  })
  const sxOrderCancelled = {
    textDecoration: 'line-through',
    color: 'text.secondary',
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" mt={3}>
        <Typography variant="h5" sx={orderCancelled ? sxOrderCancelled : {}}>
          {t('global.label.total')}
        </Typography>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          {isEstimatedTotal && (
            <Typography variant="h6" sx={{ color: palette.action.active }}>
              {t('global.label.estimated')}
            </Typography>
          )}
          <Typography variant="h5" sx={orderCancelled ? sxOrderCancelled : {}}>
            {formatNumberAsPrice(order.total || 0)}
          </Typography>
        </Stack>
      </Stack>
      {!isEmpty(dueToday) && !!order.total && (
        <>
          {depositPolicies.length === 1 && (
            <Typography variant="body3" sx={{ color: palette.text.secondary, mt: 3, mb: -3 }}>
              {getDepositLabel(order)}
            </Typography>
          )}
          <Stack direction="row" justifyContent="space-between" mt={3}>
            <Typography variant="h5">{t('global.label.dueToday', { percentage: dueTodayPercentage })}</Typography>
            <Typography variant="h5">{formatNumberAsPrice(dueToday!)}</Typography>
          </Stack>
        </>
      )}
      {orderConfirmed && (
        <Stack direction="row" justifyContent="space-between" mt={3}>
          {!!paidTodayPercentage && (
            <Typography variant="h5">{t('global.label.paidToday', { percentage: paidTodayPercentage })}</Typography>
          )}
          <Typography variant="h5">{formatNumberAsPrice(paidToday)}</Typography>
        </Stack>
      )}
      {depositPolicies.length > 1 && <DepositPoliciesDropdown order={order} receipt={receipt} />}
      {(orderCancelled || orderConfirmed) && <Divider sx={{ mt: 2, borderStyle: 'dashed' }} />}
      <Stack mt={deposits.length || invoicesWithRefund?.length ? 2 : 0} spacing={0.5}>
        {!!deposits.length && (
          <Typography variant="body2bold" sx={{ color: 'text.secondary' }}>
            {t('global.label.payments')}
          </Typography>
        )}
        {deposits.map((deposit) => {
          const surcharge =
            deposit.transactions?.reduce((sum, t) => sum + (t.type === 'CHARGE' ? (t.surcharge ?? 0) : 0), 0) ?? 0
          const surchargeTax =
            deposit.transactions?.reduce((sum, t) => sum + (t.type === 'CHARGE' ? (t.surchargeTax ?? 0) : 0), 0) ?? 0
          return (
            <Stack key={deposit.id} spacing={0.5}>
              <Stack spacing={0.5} direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body1" component="span">
                    {t('global.label.deposit')}
                  </Typography>
                  <Stack px={0.5}>
                    <Circle sx={{ width: 4, height: 4, color: palette.text.secondaryShade60 }} />
                  </Stack>
                  <Typography variant="body1" component="span" sx={{ color: 'text.secondary' }}>
                    {paymentType(deposit.payFacType, deposit.appliedStoredPaymentMethod)}
                  </Typography>
                </Stack>
                <Typography variant="body1" component="span">
                  {formatNumberAsPrice(deposit.depositDue || 0)}
                </Typography>
              </Stack>
              {!!surcharge && (
                <Stack direction="row" pl={9.5} justifyContent="space-between">
                  <Typography variant="body1" component="span" sx={{ color: 'text.secondary' }}>
                    {receipt.surchargeDisplayName}
                  </Typography>
                  <Typography variant="body1" component="span">
                    {formatNumberAsPrice(surcharge)}
                  </Typography>
                </Stack>
              )}
              {!!surchargeTax && (
                <Stack direction="row" pl={9.5} justifyContent="space-between">
                  <Typography variant="body1" component="span" sx={{ color: 'text.secondary' }}>
                    {receipt.surchargeDisplayName} Tax
                  </Typography>
                  <Typography variant="body1" component="span">
                    {formatNumberAsPrice(surchargeTax)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )
        })}
        {invoicesWithRefund?.map((invoice) => (
          <Stack key={`${invoice.displayName}-${invoice.id}`}>
            {calculateInvoicePayFacTotals(invoiceDepositTxns.get(invoice.id || ''), true, true)
              .filter(([, amount]) => amount)
              .map(([payFacType, amount]) => (
                <Stack direction="row" justifyContent="space-between" key={`${payFacType}-${invoice.id}`}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body1bold" color="error.main">
                      {orderCancelled ? t('receipt.label.cancellationRefund') : t('global.label.refund')}
                    </Typography>
                    <Stack px={0.5}>
                      <Circle sx={{ width: 4, height: 4, color: palette.text.secondaryShade60 }} />
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                      <Typography variant="body1" color="text.secondary" key={payFacType} component="span">
                        {paymentType(payFacType, storedPaymentMethod)}
                      </Typography>
                    </Typography>
                  </Stack>
                  <Typography variant="body1bold" component="span" color="error.main">
                    ({formatNumberAsPrice(amount || 0)})
                  </Typography>
                </Stack>
              ))}
          </Stack>
        ))}
      </Stack>
      {orderConfirmed && (
        <>
          <Spacer height={3} />
          <Alert
            severity="info"
            icon={false}
            sx={{
              p: 1,
              borderRadius: 1,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" color="primary.default" component="span">
                {t('global.label.dueAtVisit')}
                <Box component="span" sx={{}}>
                  *
                </Box>
              </Typography>
              <Typography variant="h5" color="primary.default">
                {formatNumberAsPrice(order.amountDue || 0)}
              </Typography>
            </Stack>
          </Alert>
          <Spacer height={2} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
            <Box component="span" sx={{ mr: 0.5 }}>
              *
            </Box>
            {t('receipt.label.dueAtVisitDisclaimer')}
          </Typography>
        </>
      )}
    </>
  )
}
